import { api, ApiError } from "./client"
import type { ImageEvaluationResult, ImageUploadResult } from "./agents"
import type { ApiEstimate, ApiVehicle, VehicleSide, VehicleType } from "./types"
import type { IntakeConfig } from "@/lib/intake/intake-config"
import {
  isIntakeSurface,
  type IntakeDevice,
  type IntakeSurface,
} from "@/components/estimates/consumer-intake-estimate/intake-flow-types"

// Public, no-login consumer flow API. MOCK-ONLY — none of these endpoints
// exist on the real backend yet (see design-log). The api client attaches the
// auth token only when present and never redirects /public/* on 401, so these
// calls work for an unauthenticated consumer.
//
// Lifecycle (contact-first): createIntake (lead exists, no detection) →
// appendImages (AI read) → updateDetails per extra → bookAppointment.
// Desktop hands the same lead to a phone via sendHandoff / continueIntake.

// What the consumer surface is allowed to see of the shop's intake config —
// notify/sharedAt/updatedAt are stripped server-side.
export type IntakeConfigPublic = Pick<
  IntakeConfig,
  "enabled" | "welcome" | "extras" | "required" | "consent" | "kiosk"
>

export interface PublicShopProfile {
  id: number
  name: string
  type: string
  street: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  phoneNumber: string | null
  branding: {
    primaryColor: string
    logoUrl: string | null
    logoInitials: string
    hours: string
  }
  intake: IntakeConfigPublic
}

export interface PublicSchedulingRules {
  inspection_days: number[]
  dropoff_days: number[]
  dropoff_buffer_days: number
  inspection_start_hour: number
  inspection_end_hour: number
  inspection_slot_duration: number
  dropoff_start_hour: number
  dropoff_end_hour: number
}

// Which step of the consumer's photo walk a photo answers. Stored on
// image.metadata.shot (mock passthrough — the real AddEstimateImageDto has no
// metadata yet). A newer photo with the same id retires the older one.
export type IntakeShotKind = "wide" | "closer" | "closeup" | "sticker" | "extra"

export interface IntakeShotRef {
  id: string
  kind: IntakeShotKind
  zone: string | null
  // How the live camera took it (for tuning the camera from real use).
  capture?: IntakeShotCapture
}

export interface IntakeShotCapture {
  // How long the camera was up for this photo before the shutter (ms; cards
  // and sheets over it don't count).
  lockMs: number | null
  // Where the camera's models ran: a worker, on the page, or none (guided).
  vision: "worker" | "inline" | null
  detectMs: number | null
  segmentMs: number | null
  viewMs: number | null
  orientation: "upright" | "sideways"
  // The phone's own still photo (Android Chrome), or the sharpest of a
  // burst of video frames (everywhere else, or when a still failed).
  photo: "still" | "burst"
  // The usual lens, or the wide one (0.5×) for want of room to back up.
  lens: "normal" | "wide"
}

export interface IntakeImagePayload {
  imageUrl: string
  side: VehicleSide
  qualityScore: number
  qualified: boolean
  description?: string
  metadata?: { shot: IntakeShotRef }
}

export interface IntakeConsent {
  text: string
  acceptedAt: string
  smsOptIn: boolean
}

// vin-scan: the door sticker's barcode, read live by the phone.
export type VehicleDecodedFrom = "vin-scan" | "vin-photo" | "manual" | "plate"

// Vehicle identity as the consumer gave it. vin/year/make/model/trim/type/
// color land on estimate.vehicle; plate + decodedFrom are intake-only and
// live under metadata.intake.vehicle.
export interface IntakeVehiclePatch {
  vin?: string
  year?: string
  make?: string
  model?: string
  trim?: string
  // An empty string (or null type) clears the field on the lead.
  type?: VehicleType | null
  color?: string
  plate?: string
  decodedFrom?: VehicleDecodedFrom | null
}

export interface CreateIntakePayload {
  customer: { name: string; phone: string; email?: string }
  surface: IntakeSurface
  device: IntakeDevice
  consent: IntakeConsent
  referrer?: string
  utm?: Record<string, string>
  vehicle?: IntakeVehiclePatch
  images?: IntakeImagePayload[]
}

export interface CreateIntakeResult {
  estimateId: number
  trackToken: string
  // `${origin}/go/${shopId}?c=${trackToken}&src=sms` — the phone continuation.
  continueUrl: string
}

export type IntakeDrivable = "yes" | "limp" | "no"
export type IntakeCause =
  | "rear_ended"
  | "hit_something"
  | "hit_parked"
  | "side_scrape"
  | "hail"
  | "other"
export type IntakeInsuranceMode = "insurance" | "out_of_pocket" | "unsure"
export type IntakeContactChannel = "text" | "call" | "email"
export type IntakeBestTime = "morning" | "afternoon" | "evening" | "anytime"

export interface IntakeSituation {
  drivable: IntakeDrivable | null
  cause: IntakeCause | null
  note: string
}

export interface IntakeInsurance {
  mode: IntakeInsuranceMode | null
  carrier: string
  claimNumber: string
}

export interface IntakeContactPref {
  channel: IntakeContactChannel | null
  bestTime: IntakeBestTime | null
}

// PATCH body — each present key replaces that block wholesale (idempotent:
// the reach step re-sends the whole blob + extrasDoneAt).
export interface IntakeDetailsPatch {
  customer?: { name?: string; phone?: string; email?: string }
  vehicle?: IntakeVehiclePatch
  situation?: IntakeSituation
  insurance?: IntakeInsurance
  contactPref?: IntakeContactPref
  damageZones?: string[]
  extrasDoneAt?: string
  revealSeenAt?: string
}

export interface IntakeHandoff {
  continueUrl: string
  smsSentAt: string[]
  continuedAt: string | null
  expiresAt: string
}

export interface HandoffResult {
  continueUrl: string
  // Masked, e.g. "(•••) •••-4567".
  sentTo: string
  expiresAt: string
}

// What the mock stamps on estimate.metadata.intake — read back by the flow
// (continuation), the shop's pipeline/room/estimate detail, and settings stats.
export interface IntakeMeta {
  surface: IntakeSurface
  device: IntakeDevice
  consent: IntakeConsent | null
  referrer: string | null
  utm: Record<string, string> | null
  handoff: IntakeHandoff | null
  vehicle: { plate: string | null; decodedFrom: VehicleDecodedFrom | null } | null
  situation: IntakeSituation | null
  insurance: IntakeInsurance | null
  contactPref: IntakeContactPref | null
  // null = the consumer never answered "Where's the damage?"; [] = "Not sure".
  damageZones: string[] | null
  // First time the consumer sent their photos (final append).
  photosDoneAt: string | null
  extrasDoneAt: string | null
  revealSeenAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null
}

// Null unless the estimate came through consumer intake and carries the
// intake block. Tolerant of partial blocks (older rows, half-written patches).
export function readIntakeMeta(estimate: Pick<ApiEstimate, "metadata">): IntakeMeta | null {
  const meta = estimate.metadata
  if (!isRecord(meta) || meta.source !== "consumer_intake") return null
  const intake = isRecord(meta.intake) ? meta.intake : null
  if (!intake) return null

  const surface: IntakeSurface = isIntakeSurface(intake.surface) ? intake.surface : "link"
  const device: IntakeDevice = intake.device === "desktop" ? "desktop" : "phone"

  const consentRaw = isRecord(intake.consent) ? intake.consent : null
  const consent: IntakeConsent | null = consentRaw
    ? {
        text: str(consentRaw.text) ?? "",
        acceptedAt: str(consentRaw.acceptedAt) ?? "",
        smsOptIn: consentRaw.smsOptIn === true,
      }
    : null

  const handoffRaw = isRecord(intake.handoff) ? intake.handoff : null
  const handoff: IntakeHandoff | null = handoffRaw
    ? {
        continueUrl: str(handoffRaw.continueUrl) ?? "",
        smsSentAt: Array.isArray(handoffRaw.smsSentAt)
          ? handoffRaw.smsSentAt.filter((s): s is string => typeof s === "string")
          : [],
        continuedAt: str(handoffRaw.continuedAt),
        expiresAt: str(handoffRaw.expiresAt) ?? "",
      }
    : null

  const vehicleRaw = isRecord(intake.vehicle) ? intake.vehicle : null
  const decodedRaw = str(vehicleRaw?.decodedFrom)
  const decodedFrom: VehicleDecodedFrom | null =
    decodedRaw === "vin-photo" || decodedRaw === "manual" || decodedRaw === "plate"
      ? decodedRaw
      : null
  const vehicle = vehicleRaw ? { plate: str(vehicleRaw.plate), decodedFrom } : null

  const utmRaw = isRecord(intake.utm) ? intake.utm : null
  const utm = utmRaw
    ? Object.fromEntries(
        Object.entries(utmRaw).filter((e): e is [string, string] => typeof e[1] === "string"),
      )
    : null

  return {
    surface,
    device,
    consent,
    referrer: str(intake.referrer),
    utm,
    handoff,
    vehicle,
    situation: isRecord(intake.situation) ? (intake.situation as unknown as IntakeSituation) : null,
    insurance: isRecord(intake.insurance) ? (intake.insurance as unknown as IntakeInsurance) : null,
    contactPref: isRecord(intake.contactPref)
      ? (intake.contactPref as unknown as IntakeContactPref)
      : null,
    damageZones: Array.isArray(intake.damageZones)
      ? intake.damageZones.filter((z): z is string => typeof z === "string")
      : null,
    photosDoneAt: str(intake.photosDoneAt),
    extrasDoneAt: str(intake.extrasDoneAt),
    revealSeenAt: str(intake.revealSeenAt),
    createdAt: str(intake.createdAt),
    updatedAt: str(intake.updatedAt),
  }
}

export interface PublicAppointmentPayload {
  type: "inspection" | "drop_off"
  date: string
  time: string
}

export interface PublicAppointmentResult {
  appointmentId: string
  status: "Scheduled"
}

interface PublicVinFromImageResponse {
  img: ImageUploadResult
  vin: string | null
}

// What the VIN decode actually fills in (NHTSA via the mock; the real BE's
// /vehicles/search/vin returns a full ApiVehicle — this is the subset the
// consumer flow reads).
export type DecodedVehicle = Pick<
  ApiVehicle,
  "vin" | "make" | "model" | "year" | "trim" | "type" | "color"
>

// Photo calls give up after a while on a stalled mobile connection instead of
// holding an upload slot forever (the caller retries).
const PHOTO_TIMEOUT_MS = 45_000
function photoTimeout(): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
    ? AbortSignal.timeout(PHOTO_TIMEOUT_MS)
    : undefined
}

export const publicConsumerApi = {
  getShop: (shopId: number | string) =>
    api.get<PublicShopProfile>(`/public/shops/${shopId}`),

  // Unauthenticated image evaluation. `expectedSide` is honoured by the mock
  // so guided capture doesn't false-flag the wrong side (the real BE detects
  // the side itself); `kind` only words the mock's description.
  evaluateImage: (file: File, expectedSide?: VehicleSide | null, kind?: IntakeShotKind) => {
    const formData = new FormData()
    formData.append("image", file)
    if (expectedSide) formData.append("side", expectedSide)
    if (kind) formData.append("kind", kind)
    return api.post<ImageEvaluationResult>(
      "/public/agents/images/evaluate",
      formData,
      photoTimeout(),
    )
  },

  getSchedulingRules: (shopId: number | string) =>
    api.get<PublicSchedulingRules>(`/public/shops/${shopId}/scheduling-rules`),

  // Contact-first: creates the lead (no AI read yet). Idempotent on
  // (shop, phone) within 5 min; 409 when the shop disabled intake.
  createIntake: (shopId: number | string, payload: CreateIntakePayload) =>
    api.post<CreateIntakeResult>(`/public/shops/${shopId}/intake`, payload),

  getEstimate: (trackToken: string) =>
    api.get<ApiEstimate>(`/public/estimates/${trackToken}`),

  // Photos arrive after the lead exists. final:false attaches each shot as it
  // is taken (desktop mirror + phone resume); final:true attaches anything
  // missing and runs the AI read once (empty batch allowed). Server dedupes by
  // imageUrl and retires an older photo with the same shot id.
  appendImages: (
    trackToken: string,
    images: IntakeImagePayload[],
    options: { final: boolean },
  ) =>
    api.post<ApiEstimate>(
      `/public/estimates/${trackToken}/images`,
      { images, final: options.final },
      photoTimeout(),
    ),

  // The consumer removed a photo from Review (MOCK-ONLY route): retire it.
  removeImage: (trackToken: string, imageUrl: string) =>
    api.delete<ApiEstimate>(`/public/estimates/${trackToken}/images`, { imageUrl }, photoTimeout()),

  // Skippable extras + contact edits; each key replaces its block.
  updateDetails: (trackToken: string, patch: IntakeDetailsPatch) =>
    api.patch<ApiEstimate>(`/public/estimates/${trackToken}/details`, patch),

  // "We texted you the link" (desktop), "text me a link to add photos later",
  // resend. Mock logs the SMS.
  sendHandoff: (trackToken: string) =>
    api.post<HandoffResult>(`/public/estimates/${trackToken}/handoff`, { channel: "sms" }),

  // Phone that scanned/tapped resumes the same lead. 410 after expiry.
  continueIntake: (trackToken: string) =>
    api.post<ApiEstimate>(`/public/estimates/${trackToken}/continue`, {}),

  bookAppointment: (trackToken: string, payload: PublicAppointmentPayload) =>
    api.post<PublicAppointmentResult>(
      `/public/estimates/${trackToken}/appointment`,
      payload,
    ),

  // VIN-sticker photo → VIN, no login (public twin of vinApi.fromImage).
  // A null VIN collapses to a thrown 422 so callers keep one catch path.
  vinFromImage: async (file: File): Promise<{ vin: string }> => {
    const formData = new FormData()
    formData.append("image", file)
    const result = await api.post<PublicVinFromImageResponse>(
      "/public/agents/images/vin",
      formData,
    )
    if (!result.vin) {
      throw new ApiError(422, "Couldn't find a VIN in the image")
    }
    return { vin: result.vin }
  },

  // Decode a typed VIN (public twin of vinApi.searchByVin; 422 on miss).
  decodeVin: (vin: string) =>
    api.get<DecodedVehicle>(`/public/vehicles/vin/${encodeURIComponent(vin)}`),

  // MOCK-ONLY demo: advance the repair lifecycle to reach Completed/cost.
  devAdvance: (trackToken: string) =>
    api.post<ApiEstimate>(`/public/estimates/${trackToken}/_advance`, {}),
}
